// Vetted source hierarchy. The engine never trusts "the whole internet"; it
// pulls from a curated, tiered list of vendors, each behind a normalizing
// adapter (SPEC section 10.1, "brand adapters"). Tier 1 = we lead with these.
//
// status:
//   connected = we hold real-valued, normalized data for it today (the
//               curated seed catalogs map to these).
//   planned   = vetted and in the registry, adapter not built yet.
//
// kind = how data would actually arrive. NOTE: brand color apps (Benjamin
// Moore Color Portfolio, Sherwin-Williams ColorSnap) are consumer apps, not
// open APIs, so their color values come from PUBLISHED fan-deck data, not
// scraping. Retail (Home Depot / Lowe's) expose affiliate product feeds.
// Refacing vendors are direct catalogs. This keeps ingestion legitimate.

export type Category = "paint" | "flooring" | "cabinet" | "hardware" | "decor";

export interface VendorSource {
  id: string;
  name: string;
  categories: Category[];
  tier: 1 | 2 | 3;
  status: "connected" | "planned";
  kind: "curated-color" | "official-app" | "retail-affiliate" | "reface-vendor" | "distributor-feed" | "manual";
  note?: string;
}

export const SOURCES: VendorSource[] = [
  // Paint: lead with the two most-referenced fan decks.
  { id: "benjamin-moore", name: "Benjamin Moore", categories: ["paint"], tier: 1, status: "connected", kind: "curated-color", note: "Fan-deck LRV/temperature values. Color Portfolio app is consumer-only, no open API." },
  { id: "sherwin-williams", name: "Sherwin-Williams", categories: ["paint"], tier: 1, status: "connected", kind: "curated-color", note: "Fan-deck values. ColorSnap match/color apps are consumer-only." },
  { id: "behr", name: "Behr", categories: ["paint"], tier: 2, status: "connected", kind: "curated-color", note: "Stocked at Home Depot." },

  // Flooring: curated generic set today; real vendors vetted and queued.
  { id: "curated-flooring", name: "Curated Flooring", categories: ["flooring"], tier: 1, status: "connected", kind: "manual", note: "Generic, unbranded starter set across categories." },
  { id: "mohawk", name: "Mohawk", categories: ["flooring"], tier: 1, status: "planned", kind: "distributor-feed" },
  { id: "shaw", name: "Shaw", categories: ["flooring"], tier: 2, status: "planned", kind: "distributor-feed" },
  { id: "floor-and-decor", name: "Floor & Decor", categories: ["flooring"], tier: 1, status: "planned", kind: "retail-affiliate", note: "Broad tile/stone/LVP selection to browse." },

  // Cabinet refacing: the vendors you scoped.
  { id: "cabmod", name: "CabMod", categories: ["cabinet"], tier: 1, status: "planned", kind: "reface-vendor" },
  { id: "refacekit", name: "RefaceKit", categories: ["cabinet"], tier: 1, status: "planned", kind: "reface-vendor" },
  { id: "barker", name: "Barker Door", categories: ["cabinet"], tier: 2, status: "planned", kind: "reface-vendor" },

  // Big-box retail: hardware + broad catalog via affiliate feeds.
  { id: "home-depot", name: "The Home Depot", categories: ["hardware", "flooring", "paint"], tier: 1, status: "planned", kind: "retail-affiliate" },
  { id: "lowes", name: "Lowe's", categories: ["hardware", "flooring"], tier: 1, status: "planned", kind: "retail-affiliate" },

  // Decor: aspirational vs. value ends of the shelf.
  { id: "restoration-hardware", name: "Restoration Hardware", categories: ["decor"], tier: 2, status: "planned", kind: "manual" },
  { id: "tj-maxx", name: "TJ Maxx", categories: ["decor"], tier: 3, status: "planned", kind: "manual", note: "Rotating stock; good for inexpensive accent pieces." },
];

export function sourcesFor(cat: Category): VendorSource[] {
  return SOURCES.filter((s) => s.categories.includes(cat)).sort((a, b) => a.tier - b.tier);
}

export function sourceById(id: string): VendorSource | undefined {
  return SOURCES.find((s) => s.id === id);
}
